/**
 * @fileoverview Clients home page
 * @description Calm directory workspace: search, filters, invites, and client rows.
 * @module routes/clients/ClientList
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarClock,
  RotateCw,
  Search,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/useToast';
import {
  type ClientInvitationRow,
  useInvitations,
  useRegenerateInvitationCode,
  useRevokeInvitation,
} from '@/queries/useInvitations';
import { type TrainerClientSummary, useTrainerClients } from '@/queries/useTrainerClients';
import { clientAttentionReason, clientStatus } from '@/lib/client-signals';
import { formatDate } from '@/lib/utils';
import { useUIStore } from '@/stores/ui';

type DirectoryFilter = 'all' | 'attention' | 'no-program' | 'on-track';

type ClientView = {
  id: string;
  name: string;
  email: string;
  programName: string | null;
  adherence: number | null;
  lastWorkoutDate: string | null;
  consecutiveMissed: number;
  noProgram: boolean;
  needsAttention: boolean;
  /** Not flagged, but the latest refresh failed, so it is not proven on track. */
  statusUnknown: boolean;
  /** Null when the backend could not count them. */
  formChecksToReview: number | null;
};

type InvitationView = {
  id: string;
  name: string;
  email: string;
  expiresAt: string | null;
};

function getInitials(name: string, email: string): string {
  const source = name.trim() || email.trim();
  if (!source) return 'CL';
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/**
 * `statusStale` is true when the latest refresh failed: the cached row is still shown,
 * but its status is not presented as current.
 */
function toClientView(client: TrainerClientSummary, statusStale: boolean): ClientView {
  const noProgram = !client.program_name;
  const status = statusStale
    ? { kind: 'unknown' as const }
    : clientStatus({
        hasProgram: !noProgram,
        consecutiveMissed: client.consecutive_missed,
        complianceRate: client.compliance_rate,
        adherenceUnknown: false,
      });

  return {
    id: client.id,
    name: client.display_name ?? client.email ?? 'Client',
    email: client.email ?? '—',
    programName: client.program_name,
    adherence: client.compliance_rate,
    lastWorkoutDate: client.last_workout_date,
    consecutiveMissed: client.consecutive_missed,
    noProgram,
    needsAttention: status.kind === 'attention',
    statusUnknown: status.kind === 'unknown',
    formChecksToReview: client.form_checks_to_review === undefined ? 0 : client.form_checks_to_review,
  };
}

function toInvitationView(invitation: ClientInvitationRow): InvitationView {
  return {
    id: invitation.id,
    name: invitation.client_name ?? invitation.email ?? 'Pending invite',
    email: invitation.email ?? '—',
    expiresAt: invitation.expires_at,
  };
}

function attentionReason(client: ClientView): string {
  return clientAttentionReason({
    hasProgram: !client.noProgram,
    consecutiveMissed: client.consecutiveMissed,
    complianceRate: client.adherence,
  }) ?? '';
}

function matchesSearch(
  query: string,
  fields: Array<string | null | undefined>,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((field) => (field ?? '').toLowerCase().includes(q));
}

function isOnTrack(client: ClientView): boolean {
  return !client.needsAttention && !client.statusUnknown;
}

function countForFilter(clients: ClientView[], filter: DirectoryFilter): number {
  if (filter === 'attention') return clients.filter((c) => c.needsAttention).length;
  if (filter === 'no-program') return clients.filter((c) => c.noProgram).length;
  if (filter === 'on-track') return clients.filter(isOnTrack).length;
  return clients.length;
}

function filterLabel(filter: DirectoryFilter): string {
  if (filter === 'attention') return 'Needs attention';
  if (filter === 'no-program') return 'Without a program';
  if (filter === 'on-track') return 'On track';
  return 'All clients';
}

export function ClientList(): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<DirectoryFilter>('all');
  const { setInviteModalOpen } = useUIStore();
  const clientsQuery = useTrainerClients();
  const invitationsQuery = useInvitations();
  const regenerateInvitationCode = useRegenerateInvitationCode();
  const revokeInvitation = useRevokeInvitation();

  const clients = useMemo(
    () => (clientsQuery.data ?? []).map((client) => toClientView(client, clientsQuery.isError)),
    [clientsQuery.data, clientsQuery.isError],
  );
  // Until a read succeeds (still loading, or failed) counts are unknown, never a false zero.
  const clientsUnavailable = clientsQuery.data === undefined;
  const invitationsUnavailable = invitationsQuery.data === undefined;

  const invitations = useMemo(
    () =>
      (invitationsQuery.data ?? [])
        .filter((invitation) => invitation.status === 'pending')
        .map(toInvitationView),
    [invitationsQuery.data],
  );

  const stats = useMemo(
    () => ({
      activeClients: clients.length,
      pendingInvites: invitations.length,
      noProgram: clients.filter((c) => c.noProgram).length,
      needsAttention: clients.filter((c) => c.needsAttention).length,
    }),
    [clients, invitations],
  );

  const filteredClients = useMemo(() => {
    return clients
      .filter((client) =>
        matchesSearch(search, [client.name, client.email, client.programName]),
      )
      .filter((client) => {
        if (filter === 'attention') return client.needsAttention;
        if (filter === 'no-program') return client.noProgram;
        if (filter === 'on-track') return isOnTrack(client);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, filter, search]);

  const filteredInvites = useMemo(() => {
    return invitations.filter((invite) =>
      matchesSearch(search, [invite.name, invite.email]),
    );
  }, [invitations, search]);

  async function handleResend(invitationId: string): Promise<void> {
    try {
      const code = await regenerateInvitationCode.mutateAsync(invitationId);
      toast({
        title: 'Invitation refreshed',
        description: `New invite code: ${code}`,
      });
    } catch (error) {
      toast({
        title: 'Could not resend invitation',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  }

  async function handleRevoke(invitationId: string): Promise<void> {
    try {
      await revokeInvitation.mutateAsync(invitationId);
      toast({ title: 'Invitation revoked' });
    } catch (error) {
      toast({
        title: 'Could not revoke invitation',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  }

  const isLoading = clientsQuery.isLoading || invitationsQuery.isLoading;
  const error = clientsQuery.error ?? invitationsQuery.error;

  const directoryFilters = [
    ['all', 'Everyone'],
    ['attention', 'Needs attention'],
    ['no-program', 'No program'],
    ['on-track', 'On track'],
  ] as const;

  return (
    <div className="flex min-h-full flex-col bg-background text-neutral-900">
      <div className="border-b border-neutral-200 px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-medium tracking-tight text-neutral-900 sm:text-2xl">
              Client workspace
            </h1>
            <p className="mt-0.5 text-sm text-neutral-500">
              Browse, filter, and open a client.
            </p>
          </div>
          <Button
            onClick={() => setInviteModalOpen(true)}
            className="h-10 bg-neutral-900 text-white hover:bg-neutral-800"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Invite client
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 xl:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden border-r border-neutral-200 bg-[#f0efe9]/60 px-4 py-5 xl:block">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
            Overview
          </p>
          <dl className="mt-3 space-y-2.5">
            <OverviewRow label="Active clients" value={clientsUnavailable ? null : stats.activeClients} />
            <OverviewRow label="Pending invites" value={invitationsUnavailable ? null : stats.pendingInvites} />
            <OverviewRow label="No program" value={clientsUnavailable ? null : stats.noProgram} />
            <OverviewRow label="Needs attention" value={clientsUnavailable ? null : stats.needsAttention} />
          </dl>

          <p className="mt-7 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
            Directory
          </p>
          <div
            className="mt-2 space-y-1"
            role="group"
            aria-label="Client filters"
          >
            {directoryFilters.map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
                className={[
                  'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2',
                  filter === key
                    ? 'bg-white font-medium text-neutral-900 shadow-sm'
                    : 'text-neutral-600 hover:bg-white/60',
                ].join(' ')}
              >
                <span>{label}</span>
                <span className="tabular-nums text-neutral-400">
                  {clientsUnavailable ? '—' : countForFilter(clients, key)}
                </span>
              </button>
            ))}
          </div>
        </aside>

        <div className="min-w-0 px-4 py-5 sm:px-6">
          <div className="relative max-w-lg">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, email, or program"
              className="h-11 border-neutral-200 bg-white pl-10 text-neutral-900 shadow-none placeholder:text-neutral-400"
            />
          </div>

          <div
            className="mt-3 flex gap-2 overflow-x-auto pb-1 xl:hidden"
            role="group"
            aria-label="Client filters"
          >
            {directoryFilters.map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
                className={[
                  'shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2',
                  filter === key
                    ? 'bg-neutral-900 text-white'
                    : 'bg-white text-neutral-600 ring-1 ring-neutral-200',
                ].join(' ')}
              >
                {label}
                <span className="ml-1.5 tabular-nums opacity-70">
                  {clientsUnavailable ? '—' : countForFilter(clients, key)}
                </span>
              </button>
            ))}
          </div>

          {error != null && (
            <div role="alert" className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <span>{error instanceof Error ? error.message : 'Failed to load clients'}</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={clientsQuery.isFetching || invitationsQuery.isFetching}
                onClick={() => {
                  void clientsQuery.refetch();
                  void invitationsQuery.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-800" />
            </div>
          )}

          {!isLoading && (
            <>
              {filteredInvites.length > 0 && (
                <section className="mt-6">
                  <div className="mb-2 flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 text-neutral-500" />
                    <h2 className="text-sm font-medium text-neutral-800">
                      Pending invitations
                    </h2>
                    <span className="text-xs text-neutral-400">{filteredInvites.length}</span>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-1">
                    {filteredInvites.map((invite) => (
                      <div
                        key={invite.id}
                        className="min-w-[240px] flex-1 rounded-lg border border-dashed border-neutral-300 bg-white p-3"
                      >
                        <p className="truncate text-sm font-medium text-neutral-900">
                          {invite.name}
                        </p>
                        <p className="truncate text-xs text-neutral-500">{invite.email}</p>
                        <p className="mt-1 text-xs text-neutral-400">
                          Expires {invite.expiresAt ? formatDate(invite.expiresAt) : '—'}
                        </p>
                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-neutral-200 bg-white text-xs"
                            disabled={regenerateInvitationCode.isPending}
                            onClick={() => void handleResend(invite.id)}
                          >
                            <RotateCw className="mr-1 h-3 w-3" />
                            Resend
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-neutral-200 bg-white text-xs text-red-700"
                            disabled={revokeInvitation.isPending}
                            onClick={() => void handleRevoke(invite.id)}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Revoke
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {!clientsUnavailable && (
                <section className="mt-8">
                  <div className="mb-3 flex items-baseline justify-between">
                    <h2 className="text-sm font-medium text-neutral-800">
                      {filterLabel(filter)}
                    </h2>
                    <span className="text-xs text-neutral-400">
                      {filteredClients.length} shown
                    </span>
                  </div>

                  {filteredClients.length === 0 ? (
                    <div className="rounded-xl border border-neutral-200 bg-white px-6 py-16 text-center">
                      <p className="text-base font-medium text-neutral-900">
                        {search ? 'No clients match' : 'No clients in this view'}
                      </p>
                      <p className="mt-1 text-sm text-neutral-500">
                        {search
                          ? 'Adjust search or filters.'
                          : 'Invite a client to populate your directory.'}
                      </p>
                      {!search && (
                        <Button
                          onClick={() => setInviteModalOpen(true)}
                          className="mt-4 bg-neutral-900 text-white hover:bg-neutral-800"
                        >
                          <UserPlus className="mr-2 h-4 w-4" />
                          Invite client
                        </Button>
                      )}
                    </div>
                  ) : (
                    <ul className="divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-200 bg-white">
                      {filteredClients.map((client) => (
                        <li key={client.id}>
                          <Link
                            to={`/clients/${client.id}`}
                            className="group flex items-center gap-4 px-4 py-4 transition hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-neutral-900 sm:px-5"
                          >
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-sm font-medium text-neutral-700">
                              {getInitials(client.name, client.email)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="truncate font-medium text-neutral-900">
                                  {client.name}
                                </p>
                                {client.needsAttention && (
                                  <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-800">
                                    {attentionReason(client)}
                                  </span>
                                )}
                                {client.statusUnknown && (
                                  <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] font-medium text-neutral-600">
                                    Status unknown
                                  </span>
                                )}
                                {client.formChecksToReview === null ? (
                                  <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[11px] font-medium text-neutral-600">
                                    Form checks unavailable
                                  </span>
                                ) : client.formChecksToReview > 0 ? (
                                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                    {client.formChecksToReview} to review
                                  </span>
                                ) : null}
                              </div>
                              <p className="truncate text-sm text-neutral-500">
                                {client.email}
                              </p>
                            </div>
                            <div className="hidden min-w-0 flex-col items-end gap-0.5 text-right md:flex">
                              <p className="text-sm text-neutral-800">
                                {client.programName ?? 'No program'}
                              </p>
                              <p className="text-xs text-neutral-500">
                                {typeof client.adherence === 'number'
                                  ? `${client.adherence}%`
                                  : '—'}{' '}
                                · Last {client.lastWorkoutDate ? formatDate(client.lastWorkoutDate) : '—'}
                              </p>
                            </div>
                            <ArrowRight className="h-4 w-4 shrink-0 text-neutral-300 transition group-hover:text-neutral-600" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OverviewRow({
  label,
  value,
}: {
  label: string;
  value: number | null;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-sm text-neutral-600">{label}</dt>
      <dd className="text-lg font-medium tabular-nums text-neutral-900">{value ?? '—'}</dd>
    </div>
  );
}
